import { isDemoMode } from '../config/environment';
import { demoApiService } from './demoApiService';

// 开发环境配置
const isDevelopment = process.env.NODE_ENV === 'development';

//const API_BASE_URL = "http://192.168.1.79:5163";//局域网部署用
const LOCAL_API_BASE_URL = "http://localhost:8080"; // 本地部署用
const PRODUCTION_API_BASE_URL = "https://talent-api-new-2025-eda5gsc8bsgvangx.eastasia-01.azurewebsites.net";// Azure 部署用

// 根据环境自动选择API地址
const API_BASE_URL = isDevelopment ? LOCAL_API_BASE_URL : PRODUCTION_API_BASE_URL;

// Token管理工具类
const TokenManager = {
    // 获取Access Token
    getAccessToken: () => {
        return localStorage.getItem('accessToken');
    },

    // 获取Refresh Token
    getRefreshToken: () => {
        return localStorage.getItem('refreshToken');
    },

    // 设置Token对
    setTokens: (accessToken, refreshToken) => {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
    },

    // 清除所有Token
    clearTokens: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        // 兼容旧版本的authToken
        localStorage.removeItem('authToken');
    },

    // 检查是否已登录
    isAuthenticated: () => {
        return !!TokenManager.getAccessToken();
    }
};

// 刷新Token的标志(防止并发刷新)
let isRefreshing = false;
let failedQueue = [];

// 处理队列中的请求
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

/**
 * 刷新Access Token
 * @returns {Promise<string>} 新的Access Token
 */
const refreshAccessToken = async () => {
    const refreshToken = TokenManager.getRefreshToken();

    if (!refreshToken) {
        throw new Error('No refresh token available');
    }

    try {
        const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
            throw new Error('Refresh token expired');
        }

        const data = await response.json();

        // 保存新的Token对
        TokenManager.setTokens(data.accessToken, data.refreshToken);

        return data.accessToken;
    } catch (error) {
        // 刷新失败,清除所有Token并跳转登录
        TokenManager.clearTokens();
        window.location.href = '/login';
        throw error;
    }
};

/**
 * 通用的请求函数 (支持自动Token刷新)
 * @param {string} endpoint - API的端点 (例如, '/api/talents')
 * @param {object} options - fetch API的配置对象 (例如, method, body, headers)
 * @param {boolean} isRetry - 是否为重试请求
 * @returns {Promise<any>} - 返回解析后的JSON数据
 */
const request = async (endpoint, options = {}, isRetry = false) => {
    // 从 TokenManager 获取Access Token
    const token = TokenManager.getAccessToken();

    // 设置默认请求头
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };

    // 如果存在令牌，则添加到请求头中
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // 添加超时控制（30秒）
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
        // 发送网络请求
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...options,
            headers,
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        // 处理401未授权错误 - 尝试刷新Token
        if (response.status === 401 && !isRetry) {
            if (isRefreshing) {
                // 如果正在刷新,将请求加入队列
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(newToken => {
                        // 使用新Token重试
                        return request(endpoint, options, true);
                    })
                    .catch(err => {
                        return Promise.reject(err);
                    });
            }

            isRefreshing = true;

            try {
                // 刷新Token
                const newToken = await refreshAccessToken();

                // 处理队列中的请求
                processQueue(null, newToken);

                // 重试原请求
                return request(endpoint, options, true);
            } catch (refreshError) {
                // 刷新失败,处理队列
                processQueue(refreshError, null);
                throw refreshError;
            } finally {
                isRefreshing = false;
            }
        }

        // 如果响应状态码不是 "ok" (例如 4xx, 5xx 错误)
        if (!response.ok) {
            let errorData;
            try {
                // 尝试解析JSON格式的错误体
                errorData = await response.json();
            } catch (e) {
                // 如果错误体不是JSON，则使用状态文本
                errorData = { message: response.statusText || `请求失败，状态码: ${response.status}` };
            }
            // 从后端验证错误中提取更具体的信息
            const errorMessage = errorData.errors
                ? JSON.stringify(errorData.errors)
                : (errorData.title || (errorData.error ? `${errorData.message}: ${errorData.error}` : errorData.message));
            throw new Error(errorMessage);
        }

        // 对应后端的 204 No Content 响应 (例如 PUT, DELETE 成功后)
        if (response.status === 204) {
            return null;
        }

        // 尝试解析成功的JSON响应
        try {
            return await response.json();
        } catch (e) {
            // 如果响应体为空，也视为成功
            return null;
        }
    } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error('请求超时，请检查您的网络连接或稍后再试。');
        }
        throw error;
    }
};

/**
 * ファイルアップロードリクエスト (FormData のため Content-Type は設定しない)
 */
const uploadRequest = async (endpoint, formData, isRetry = false) => {
    const token = TokenManager.getAccessToken();
    const headers = {};

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        body: formData,
        headers: headers
    });

    // 处理401 - 自动刷新Token
    if (response.status === 401 && !isRetry) {
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
                .then(() => uploadRequest(endpoint, formData, true))
                .catch(err => Promise.reject(err));
        }

        isRefreshing = true;

        try {
            const newToken = await refreshAccessToken();
            processQueue(null, newToken);
            return uploadRequest(endpoint, formData, true);
        } catch (refreshError) {
            processQueue(refreshError, null);
            throw refreshError;
        } finally {
            isRefreshing = false;
        }
    }

    if (!response.ok) {
        let errorData;
        try {
            errorData = await response.json();
        } catch (e) {
            errorData = { message: response.statusText || `请求失败，状态码: ${response.status}` };
        }
        const errorMessage = errorData.errors ? JSON.stringify(errorData.errors) : (errorData.title || errorData.message);
        throw new Error(errorMessage);
    }

    return await response.json();
};

// 导出一个包含了所有API方法的对象
const liveApiService = {
    // ==================== Token管理 ====================
    TokenManager,

    // 认证相关
    login: async (username, password) => {
        const data = await request('/api/auth/login', {
            method: 'POST',
            body: JSON.stringify({ username, password }),
        });

        // 保存双Token
        if (data.accessToken && data.refreshToken) {
            TokenManager.setTokens(data.accessToken, data.refreshToken);
        }

        return data;
    },

    logout: async () => {
        const refreshToken = TokenManager.getRefreshToken();

        if (refreshToken) {
            try {
                // 尝试撤销Refresh Token
                await request('/api/auth/revoke', {
                    method: 'POST',
                    body: JSON.stringify({ refreshToken }),
                });
            } catch (error) {
                console.error('撤销Token失败:', error);
            }
        }

        // 清除本地Token
        TokenManager.clearTokens();
    },

    // 人才信息 (Talents) 相关
    getTalents: (params = {}) => {
        const query = new URLSearchParams(
            Object.entries(params).filter(([, value]) => value !== null && value !== '')
        ).toString();
        return request(`/api/talents?${query}`);
    },
    getTalentById: (id) => request(`/api/talents/${id}`),
    getTalentBillingSeries: (id, months = 36) => request(`/api/talents/${id}/billing-series?months=${months}`),
    getTalentBillingLedger: (id, options = {}) => {
        const params = new URLSearchParams();
        if (options.startMonth) params.append('startMonth', options.startMonth);
        if (options.endMonth) params.append('endMonth', options.endMonth);
        const query = params.toString();
        return request(`/api/talents/${id}/billing-ledger${query ? `?${query}` : ''}`);
    },
    upsertTalentBillingOverride: (id, payload) => request(`/api/talents/${id}/billing-overrides`, {
        method: 'PUT',
        body: JSON.stringify(payload)
    }),
    restoreTalentBillingOverride: (id, payload) => request(`/api/talents/${id}/billing-overrides/restore`, {
        method: 'POST',
        body: JSON.stringify(payload)
    }),
    // 管理費変更履歴
    getFeeChanges: (id) => request(`/api/talents/${id}/fee-changes`),
    addFeeChange: (id, payload) => request(`/api/talents/${id}/fee-changes`, {
        method: 'POST',
        body: JSON.stringify(payload)
    }),
    deleteFeeChange: (id, changeId) => request(`/api/talents/${id}/fee-changes/${changeId}`, {
        method: 'DELETE'
    }),
    getTalentEmploymentPauses: (id) => request(`/api/talents/${id}/employment-pauses`),
    updateTalentEmploymentPauses: (id, pauses = []) => request(`/api/talents/${id}/employment-pauses`, {
        method: 'PUT',
        body: JSON.stringify({ pauses })
    }),
    updateResidenceEmploymentDates: (id, payload) => request(`/api/talents/${id}/residence-employment-dates`, {
        method: 'PUT',
        body: JSON.stringify(payload)
    }),
    createTalent: (talentData) => request('/api/talents', {
        method: 'POST',
        body: JSON.stringify(talentData),
    }),
    updateTalent: (id, talentData, options = {}) => {
        const params = new URLSearchParams();
        if (options.forceBaseFeeUpdateWhenFeeChanges) {
            params.append('forceBaseFeeUpdateWhenFeeChanges', 'true');
        }
        const query = params.toString();
        return request(`/api/talents/${id}${query ? `?${query}` : ''}`, {
            method: 'PUT',
            body: JSON.stringify(talentData),
        });
    },
    deleteTalent: (id) => request(`/api/talents/${id}`, {
        method: 'DELETE'
    }),

    // 获取地图数据
    getTalentMapData: () => request('/api/talents/map-data'),
    getDashboardSummary: () => request('/api/talents/dashboard-summary'),

    // 获取Admin专属高级统计数据
    getAdminAnalytics: async (options = {}) => {
        const params = new URLSearchParams();
        if (options.startMonth) params.append('startMonth', options.startMonth);
        if (options.endMonth) params.append('endMonth', options.endMonth);
        if (options.initialCostStartMonth) params.append('initialCostStartMonth', options.initialCostStartMonth);
        if (options.initialCostEndMonth) params.append('initialCostEndMonth', options.initialCostEndMonth);
        if (options.attritionStartMonth) params.append('attritionStartMonth', options.attritionStartMonth);
        if (options.attritionEndMonth) params.append('attritionEndMonth', options.attritionEndMonth);
        if (options.attritionSalesStaffId) params.append('attritionSalesStaffId', options.attritionSalesStaffId);
        const queryString = params.toString();
        return await request(`/api/talents/admin-analytics${queryString ? `?${queryString}` : ''}`, {
            method: 'GET'
        });
    },

    // 获取Sales专属统计数据
    getSalesAnalytics: async (options = {}) => {
        const params = new URLSearchParams();
        if (options.startMonth) params.append('startMonth', options.startMonth);
        if (options.endMonth) params.append('endMonth', options.endMonth);
        if (options.initialCostStartMonth) params.append('initialCostStartMonth', options.initialCostStartMonth);
        if (options.initialCostEndMonth) params.append('initialCostEndMonth', options.initialCostEndMonth);
        const queryString = params.toString();
        return await request(`/api/talents/sales-analytics${queryString ? `?${queryString}` : ''}`, {
            method: 'GET'
        });
    },

    // 获取Accounting专属统计数据
    getAccountingAnalytics: async (options = {}) => {
        const params = new URLSearchParams();
        if (options.startMonth) params.append('startMonth', options.startMonth);
        if (options.endMonth) params.append('endMonth', options.endMonth);
        const queryString = params.toString();
        return await request(`/api/accounting/analytics${queryString ? `?${queryString}` : ''}`, {
            method: 'GET'
        });
    },

    // 在留信息 (Residence) 相关
    getResidenceByTalentId: async (talentId) => {
        try {
            return await request(`/api/talents/${talentId}/residence`);
        } catch (error) {
            // 404错误表示该人才没有在留信息，返回null而不是抛出错误
            if (error.message.includes('该人才的在留信息不存在') || error.message.includes('404') || error.message.includes('Not Found')) {
                return null;
            }
            // 403错误表示没有权限访问该人才的在留信息，返回null而不是抛出错误
            if (error.message.includes('Forbidden') || error.message.includes('403') || error.message.includes('您只能查看自己管理的人才的在留信息')) {
                console.log('No permission to access residence data for talent:', talentId);
                return null;
            }
            // 其他错误继续抛出
            throw error;
        }
    },
    createOrUpdateResidence: (talentId, data) => request(`/api/talents/${talentId}/residence`, {
        method: 'PUT',
        body: JSON.stringify(data),
    }),

    // 借款信息 (Loan) 相关
    getLoanByTalentId: (talentId) => request(`/api/talents/${talentId}/loan`),
    getAllLoansByTalentId: (talentId) => request(`/api/talents/${talentId}/loan/all`),
    completeLoan: (talentId) => request(`/api/talents/${talentId}/loan/complete`, {
        method: 'POST',
    }),
    createOrUpdateLoan: (talentId, data) => request(`/api/talents/${talentId}/loan`, {
        method: 'PUT',
        body: JSON.stringify(data),
    }),
    addLoanEntry: (talentId, data) => request(`/api/talents/${talentId}/loan/entries`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    updateLoanEntry: (talentId, entryId, data) => request(`/api/talents/${talentId}/loan/entries/${entryId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    }),
    deleteLoanEntry: (talentId, entryId) => request(`/api/talents/${talentId}/loan/entries/${entryId}`, {
        method: 'DELETE',
    }),
    getLoanEntryCategories: () => request('/api/loan-entry-categories'),
    createLoanEntryCategory: (name) => request('/api/loan-entry-categories', {
        method: 'POST',
        body: JSON.stringify({ name }),
    }),

    // 还款记录 (Repayment) 相关
    addRepayment: (loanId, repaymentData) => request(`/api/loans/${loanId}/repayments`, {
        method: 'POST',
        body: JSON.stringify(repaymentData),
    }),
    updateRepayment: (loanId, repaymentId, repaymentData) => request(`/api/loans/${loanId}/repayments/${repaymentId}`, {
        method: 'PUT',
        body: JSON.stringify(repaymentData),
    }),
    deleteRepayment: (loanId, repaymentId) => request(`/api/loans/${loanId}/repayments/${repaymentId}`, {
        method: 'DELETE',
    }),

    // 附件 (Attachments) 相关
    getAttachmentsForTalent: (talentId) => request(`/api/attachments/Talent/${talentId}`),
    uploadAttachment: async (talentId, file, sourceModule, loanEntryId = null) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('sourceModule', sourceModule);
        if (loanEntryId) {
            formData.append('loanEntryId', loanEntryId);
        }

        return uploadRequest(`/api/attachments/Upload/${talentId}`, formData);
    },
    downloadAttachment: async (attachmentId) => {
        const token = TokenManager.getAccessToken();
        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}/api/attachments/Download/${attachmentId}`, {
            headers: headers
        });

        if (!response.ok) {
            throw new Error('ダウンロードに失敗しました');
        }

        return await response.blob();
    },
    checkDuplicatePhotos: () => request('/api/attachments/CheckDuplicates'),
    deleteAttachment: (attachmentId) => request(`/api/attachments/${attachmentId}`, {
        method: 'DELETE'
    }),
    cleanupOrphanedAttachments: () => request('/api/attachments/CleanupOrphaned', {
        method: 'POST'
    }),

    // 管理员 (Admin) 相关
    getAuditLogs: () => request('/api/admin/auditlogs'),

    // OCR 相关
    recognizeResidencePermit: async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        return uploadRequest('/api/ocr/recognize-residence-permit', formData);
    },

    // 获取销售员用户列表（Admin / SuperAdmin / Accounting可用）
    getSalesUsers: async () => {
        return await request('/api/talents/sales-users', {
            method: 'GET'
        });
    },

    // 人才转让
    transferTalent: async (talentId, newSalesStaffId) => {
        return await request(`/api/talents/${talentId}/transfer`, {
            method: 'PUT',
            body: JSON.stringify({ newSalesStaffId })
        });
    },

    getJobSeekerRegistrations: async ({ page = 1, pageSize = 20 } = {}) => {
        const params = new URLSearchParams({ page, pageSize });
        return request(`/api/jobseeker-registrations?${params}`, { cache: 'no-store' });
    },
    getJobSeekerRegistration: async (id) => {
        return request(`/api/jobseeker-registrations/${encodeURIComponent(id)}`, { cache: 'no-store' });
    },
    downloadJobSeekerAttachment: async (id, kind) => {
        const token = TokenManager.getAccessToken();
        const response = await fetch(`${API_BASE_URL}/api/jobseeker-registrations/${encodeURIComponent(id)}/files/${encodeURIComponent(kind)}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            cache: 'no-store',
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.message || 'ダウンロードに失敗しました');
        }
        const disposition = response.headers.get('Content-Disposition') || '';
        const encoded = disposition.match(/filename\*\s*=\s*UTF-8'[^']*'([^;]+)/i);
        const plain = disposition.match(/(?:^|;)\s*filename\s*=\s*(?:"([^"]*)"|([^;]+))/i);
        let fileName = (plain?.[1] || plain?.[2] || '').trim();
        if (encoded) {
            try {
                fileName = decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, '')).trim() || fileName;
            } catch {
                // Malformed filename metadata must not prevent downloading the file.
            }
        }
        return { blob: await response.blob(), fileName };
    },
    deleteJobSeekerRegistration: async (id) => {
        return request(`/api/jobseeker-registrations/${encodeURIComponent(id)}`, { method: 'DELETE' });
    },

    // ==================== 取引先管理 ====================
    getClientOptions: async () => {
        return await request('/api/clients/options', {
            method: 'GET'
        });
    },
    getClientOrganizations: async (options = {}) => {
        const params = new URLSearchParams();
        if (options.search) params.append('search', options.search);
        const query = params.toString();
        return await request(`/api/clients/organizations${query ? `?${query}` : ''}`, {
            method: 'GET'
        });
    },
    getClientOrganization: async (organizationId) => {
        return await request(`/api/clients/organizations/${organizationId}`, {
            method: 'GET'
        });
    },
    getClientOrganizationTalents: async (organizationId, options = {}) => {
        const params = new URLSearchParams();
        if (options.workplaceId) params.append('workplaceId', options.workplaceId);
        if (options.search) params.append('search', options.search);
        if (options.page) params.append('page', options.page);
        if (options.pageSize) params.append('pageSize', options.pageSize);
        const query = params.toString();
        return await request(`/api/clients/organizations/${organizationId}/talents${query ? `?${query}` : ''}`, {
            method: 'GET'
        });
    },
    syncClientsFromTalents: async () => {
        return await request('/api/clients/sync-from-talents', {
            method: 'POST'
        });
    },
    createClientOrganization: async (payload) => {
        return await request('/api/clients/organizations', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    },
    updateClientOrganization: async (organizationId, payload) => {
        return await request(`/api/clients/organizations/${organizationId}`, {
            method: 'PUT',
            body: JSON.stringify(payload)
        });
    },
    deleteClientOrganization: async (organizationId) => {
        return await request(`/api/clients/organizations/${organizationId}`, {
            method: 'DELETE'
        });
    },
    createClientWorkplace: async (organizationId, payload) => {
        return await request(`/api/clients/organizations/${organizationId}/workplaces`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    },
    updateClientWorkplace: async (workplaceId, payload) => {
        return await request(`/api/clients/workplaces/${workplaceId}`, {
            method: 'PUT',
            body: JSON.stringify(payload)
        });
    },
    deleteClientWorkplace: async (workplaceId) => {
        return await request(`/api/clients/workplaces/${workplaceId}`, {
            method: 'DELETE'
        });
    },
    getOrganizationContracts: async (organizationId) => {
        return await request(`/api/client-contracts/organizations/${organizationId}`, {
            method: 'GET'
        });
    },
    uploadOrganizationContracts: async (organizationId, files) => {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        return uploadRequest(`/api/client-contracts/organizations/${organizationId}/upload`, formData);
    },
    getWorkplaceContracts: async (workplaceId) => {
        return await request(`/api/client-contracts/workplaces/${workplaceId}`, {
            method: 'GET'
        });
    },
    uploadWorkplaceContracts: async (workplaceId, files) => {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        return uploadRequest(`/api/client-contracts/workplaces/${workplaceId}/upload`, formData);
    },
    getTalentContracts: async (talentId) => {
        return await request(`/api/client-contracts/talents/${talentId}`, {
            method: 'GET'
        });
    },
    uploadTalentContracts: async (talentId, files) => {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        return uploadRequest(`/api/client-contracts/talents/${talentId}/upload`, formData);
    },
    downloadClientContract: async (contractId) => {
        const token = TokenManager.getAccessToken();
        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}/api/client-contracts/${contractId}/download`, {
            headers: headers
        });

        if (!response.ok) {
            throw new Error('ダウンロードに失敗しました');
        }

        return await response.blob();
    },
    deleteClientContract: async (contractId) => {
        return await request(`/api/client-contracts/${contractId}`, {
            method: 'DELETE'
        });
    },

    // ==================== 用户管理API (SuperAdmin专用) ====================

    // 获取所有用户列表
    getAllUsersForManagement: async () => {
        return await request('/api/admin/user-management/users', {
            method: 'GET'
        });
    },

    // 创建新用户
    createUser: async (username, password, role) => {
        return await request('/api/admin/user-management/users', {
            method: 'POST',
            body: JSON.stringify({ username, password, role })
        });
    },

    // 更新用户名
    updateUsername: async (userId, newUsername) => {
        return await request(`/api/admin/user-management/users/${userId}/username`, {
            method: 'PUT',
            body: JSON.stringify({ newUsername })
        });
    },

    // 重置用户密码
    resetUserPassword: async (userId, newPassword) => {
        return await request(`/api/admin/user-management/users/${userId}/password`, {
            method: 'PUT',
            body: JSON.stringify({ newPassword })
        });
    },

    // 删除用户
    deleteUserEnhanced: async (userId) => {
        return await request(`/api/admin/user-management/users/${userId}`, {
            method: 'DELETE'
        });
    },

    // ==================== 系统通知 ====================
    getSystemNotifications: async () => {
        return await request('/api/notifications/system', {
            method: 'GET'
        });
    },
    createSystemNotification: async (payload) => {
        return await request('/api/notifications/system', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    },
    updateSystemNotification: async (notificationId, payload) => {
        return await request(`/api/notifications/system/${notificationId}`, {
            method: 'PUT',
            body: JSON.stringify(payload)
        });
    },
    sendSystemNotification: async (notificationId) => {
        return await request(`/api/notifications/system/${notificationId}/send`, {
            method: 'POST'
        });
    },
    deleteSystemNotification: async (notificationId) => {
        return await request(`/api/notifications/system/${notificationId}`, {
            method: 'DELETE'
        });
    },
    getMySystemNotifications: async () => {
        return await request('/api/notifications/system/my', {
            method: 'GET'
        });
    },
    markSystemNotificationRead: async (notificationId) => {
        return await request(`/api/notifications/system/${notificationId}/read`, {
            method: 'POST'
        });
    },

    // ==================== 特定技能ニュース ====================
    getLatestSpecifiedSkillNews: async (limit = 8) => {
        return await request(`/api/news/latest?limit=${encodeURIComponent(limit)}`, {
            method: 'GET'
        });
    },
    getSpecifiedSkillNews: async (options = {}) => {
        const params = new URLSearchParams();
        if (options.page) params.append('page', options.page);
        if (options.pageSize) params.append('pageSize', options.pageSize);
        if (options.category) params.append('category', options.category);
        if (options.status) params.append('status', options.status);
        const query = params.toString();
        return await request(`/api/news${query ? `?${query}` : ''}`, {
            method: 'GET'
        });
    },
    markSpecifiedSkillNewsRead: async (articleId) => {
        return await request(`/api/news/${articleId}/read`, {
            method: 'POST'
        });
    },
    markAllSpecifiedSkillNewsRead: async (category = '') => {
        const query = category ? `?category=${encodeURIComponent(category)}` : '';
        return await request(`/api/news/read-all${query}`, {
            method: 'POST'
        });
    },
    archiveSpecifiedSkillNews: async (articleId) => {
        return await request(`/api/news/${articleId}/archive`, {
            method: 'POST'
        });
    },
    restoreSpecifiedSkillNews: async (articleId) => {
        return await request(`/api/news/${articleId}/restore`, {
            method: 'POST'
        });
    },
};

export const apiService = isDemoMode ? demoApiService : liveApiService;
